import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Card from '@mui/material/Card';
import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import get from 'lodash/get';
import { AnalyticItem } from '@mgm/common/components';
import ScrollBar from '@mgm/common/components/ScrollBar';
import { useAppSelector } from '../../../hooks/useAppStore';
import { getInventory, getSelectedLocationId } from '../../../store/inventory/selector';

const InventoryTotalsHeader = () => {
  const { t } = useTranslation(['commissions', 'timesheets', 'dashboard']);
  const theme = useTheme();
  const selectedLocationId = useAppSelector(getSelectedLocationId);
  const inventoryItems = useAppSelector(getInventory);

  const dailyTotals = useMemo(() => {
    if (selectedLocationId === null || selectedLocationId <= 1) {
      return {
        productsTotal: 0, variantsTotal: 0, damagedTotal: 0, almostSoldOut: 0, soldoutTotal: 0,
      };
    }

    const locationInventory = inventoryItems[selectedLocationId]?.variants;

    if (!locationInventory || locationInventory.length === 0) {
      return {
        productsTotal: 0, variantsTotal: 0, damagedTotal: 0, almostSoldOut: 0, soldoutTotal: 0,
      };
    }

    const totals = locationInventory.reduce(
      (acc, variant) => {
        acc.variantsTotal += 1;
        acc.damagedTotal += variant?.node.inventoryItem?.inventoryLevel?.quantities[3]?.quantity || 0;
        if (variant?.node.inventoryItem?.inventoryLevel?.quantities[0]?.quantity !== undefined) {
          const currentQuantity = get(variant, 'node.inventoryItem.inventoryLevel.quantities[0].quantity', 0);

          acc.almostSoldOut += currentQuantity > 0 && currentQuantity <= 5 ? 1 : 0;
          acc.soldoutTotal += currentQuantity <= 0 ? 1 : 0; // Updated logic here
        }
        return acc;
      },
      {
        productsTotal: 0, variantsTotal: 0, damagedTotal: 0, almostSoldOut: 0, soldoutTotal: 0,
      },
    );

    totals.productsTotal = locationInventory.reduce((uniqueProducts, variant) => {
      if (variant?.node.product?.id) {
        uniqueProducts.add(variant.node.product.id);
      }
      return uniqueProducts;
    }, new Set()).size;

    return totals;
  }, [selectedLocationId, inventoryItems]);

  return (
    <Card sx={{
      height: '88px',
    }}
    >
      <ScrollBar>
        <Stack
          direction="row"
          divider={<Divider orientation="vertical" flexItem sx={{ borderStyle: 'dashed' }} />}
          sx={{ p: 2, overflow: 'auto !important' }}
        >
          <AnalyticItem
            id="total-products"
            title={t('products', { ns: 'dashboard' })}
            percent={100}
            price={dailyTotals.productsTotal}
            icon="solar:bag-3-linear"
            color={theme.palette.success.main}
          />
          <AnalyticItem
            id="total-variants"
            title={t('Variants', { ns: 'dashboard' })}
            percent={100}
            price={dailyTotals.variantsTotal}
            icon="fluent-mdl2:product-variant"
            color={theme.palette.info.main}
          />
          <AnalyticItem
            id="total-damaged"
            title={t('Damaged', { ns: 'dashboard' })}
            percent={100}
            price={dailyTotals.damagedTotal}
            icon="icon-park-outline:damage-map"
            color="#581C87"
          />
          <AnalyticItem
            id="total-almost-sold-out"
            title={t('Almost sold out', { ns: 'dashboard' })}
            percent={100}
            price={dailyTotals.almostSoldOut}
            icon="qlementine-icons:task-soon-16"
            color={theme.palette.warning.main}
          />
          <AnalyticItem
            id="total-sold-out"
            title={t('Sold out', { ns: 'dashboard' })}
            percent={100}
            price={dailyTotals.soldoutTotal}
            icon="ep:sold-out"
            color={theme.palette.error.dark}
          />
        </Stack>
      </ScrollBar>
    </Card>
  );
};

export { InventoryTotalsHeader };
